import React, { useCallback, useState } from "react";
import { Box, Typography } from "@mui/material";
import FileChip, { FILE_STATUS } from "./FileChip";

const ALLOWED_MIME_TYPES = [
	"image/jpeg",
	"image/png",
	"image/webp",
	"image/gif",
	"application/pdf",
	"text/plain",
	"text/markdown",
	"text/csv",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const MAX_FILES = 5;
const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;

export default function AttachmentTray({ files, onRemove, onDrop, msg, disabled }) {
	const [isDragOver, setIsDragOver] = useState(false);

	const handleDragOver = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!disabled) setIsDragOver(true);
	}, [disabled]);

	const handleDragLeave = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(false);
	}, []);

	const handleDrop = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(false);
		if (disabled || !onDrop) return;

		const droppedFiles = Array.from(e.dataTransfer.files);
		onDrop(droppedFiles);
	}, [disabled, onDrop]);

	if (!files || files.length === 0) return null;

	const processingCount = files.filter(f =>
		[FILE_STATUS.UPLOADING, FILE_STATUS.SCANNING].includes(f.scanStatus)
	).length;

	return (
		<Box
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
			sx={{
				display: "flex",
				flexWrap: "wrap",
				gap: 0.75,
				padding: "8px 12px",
				borderRadius: "10px",
				backgroundColor: isDragOver ? "#F3E8FF" : "#FAFAFA",
				border: isDragOver ? "2px dashed #B26EFC" : "1px solid #EEEEEE",
				marginBottom: "8px",
				transition: "all 0.2s ease",
				position: "relative",
			}}
		>
			{files.map((file) => (
				<FileChip
					key={file.fileId}
					file={file}
					onRemove={onRemove}
					msg={msg}
					disabled={disabled}
				/>
			))}

			{processingCount > 0 && (
				<Typography
					variant="caption"
					sx={{
						width: "100%",
						textAlign: "center",
						fontSize: "10px",
						color: "#B26EFC",
						marginTop: "4px",
					}}
				>
					{msg("file.scanning", "Scanning...")} ({processingCount}/{files.length})
				</Typography>
			)}
		</Box>
	);
}

export { ALLOWED_MIME_TYPES, MAX_FILES, MAX_FILE_SIZE_BYTES };
